import * as React from "react";
import { ArrayFieldProps, Form, useArrayField, useFormApi } from "informed";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { TextBoxWithStaticPrefix } from "../../forms/informedv2/TextBoxWithStaticPrefix";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { WebPortalClient, StudyCountry } from "webportalclient";
import { TextAreaWithStaticPrefix } from "../../forms/informedv2/TextAreaWithStaticPrefix";
import { Link } from "react-router-dom";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import DocumentTitle from "../../misc/components/DocumentTitle";
import { Authorization } from "../../misc/components/Authorization";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { useSecurityContext } from "../../Core/SecurityContext";
import { AuditModal } from "../../auditmodal/components/AuditModal";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";

interface CountryFormModel {
    tollFreePhone: string;
    localPostalAddress: string;
}

interface SupportContactFormModel {
    countries: CountryFormModel[];
}

const SupportContactItems = () => {
    const { fields } = useArrayField({ field: "countries" });

    if (fields.length === 0)
        return (
            <div className="col-md-12">
                <p>There are no countries associated with this study.</p>
            </div>
        );

    return (
        <>
            {fields.map(({ key, field, initialValue }: ArrayFieldProps & { initialValue: { name: string } }) => (
                <div key={key} className="row">
                    <div className="col-md-12">
                        <h3>{initialValue.name}</h3>
                    </div>
                    <div className="form-group col-md-6">
                        <label>Toll Free Number</label>&nbsp;
                        <TextBoxWithStaticPrefix
                            field={`${field}.tollFreePhone`}
                            staticpart={<i className="fas fa-phone"></i>}
                            type="phone"
                        />
                    </div>
                    <div className="form-group col-md-6">
                        <label>Local Vitalograph Media Postal Address</label>&nbsp;
                        <TextAreaWithStaticPrefix
                            field={`${field}.localPostalAddress`}
                            staticpart={<i className="fas fa-envelope"></i>}
                        />
                    </div>
                </div>
            ))}
        </>
    );
}

export const SupportContactForm = ({ form, onRefresh }) => {
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const [submitting, setSubmitting] = React.useState(false);
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(undefined);
    const formApi = useFormApi<SupportContactFormModel>();
    const [showAuditModal, setShowAuditModal] = React.useState<boolean>(false);

    const onPreAuditSubmit = async () => {
        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSuccessAlert(false);
            setSubmitting(false);
            setShowAuditModal(false);
            setErrorAlert("Errors exist on the page");
            return;
        }

        setShowAuditModal(true);
    }

    const onSubmit = async () => {
        setShowAuditModal(false);
        setSubmitting(true);
        setSuccessAlert(false);
        setErrorAlert(undefined);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSuccessAlert(false);
            setSubmitting(false);
            setShowAuditModal(false);
            setErrorAlert("Errors exist on the page");
            return;
        }

        try {
            const toPatch = state.values.countries ? state.values.countries.map((s, i) => ({
                ...s,
                id: form.countries[i].id,
            })) : [];

            let patchPromises: Promise<StudyCountry>[] = [];
            
            for (const country of toPatch.filter(x => x)) {
                patchPromises.push(client.client.patchStudyCountry({
                        tollFreeNumber: country?.tollFreePhone,
                        localPostalAddress: country?.localPostalAddress
                    }, country.id, ""));
            }

            const patchResult = await Promise.all(patchPromises);
            if (patchResult.every(p => p)) {
                setSuccessAlert(true);
            }

            onRefresh();
        } catch (ex) {
            setErrorAlert(ex.message);
        }

        setSubmitting(false);
    };

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully.
            </Alert>
            <Alert show={errorAlert !== undefined} category={AlertCategoryEnum.Danger}>{errorAlert}</Alert>
            <div className="row">
                <div className="col-md-12">
                    <h2 className="text-primary">
                        Edit Support Contact Information
                    </h2>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div className="row">
                                <div className="col-md-12">
                                    <p>Here you can edit Vitalograph Support Services toll free contact telephone numbers and the Vitalograph project team study contact email address that are used for this particular study.</p>
                                </div>
                            </div>
                            <SupportContactItems />
                        </div>
                    </div>
                </div>
            </div>
            <div className="h2 pull-right">
                <Authorization currentPermissions={currentUserPermissions} studyId={study.id} permissionId={PermissionEnum.EditSupportContactInformationAdministration}>
                    <button className="btn btn-primary" type="submit" disabled={submitting} onClick={onPreAuditSubmit} data-automationid="Save">
                        Save
                    </button>&nbsp;
                </Authorization>
                <Link className="btn btn-primary btn-outline" to="/admin" data-automationid="ReturnToAdmin">
                    Cancel
                </Link>
            </div>
            {showAuditModal ? <AuditModal onSubmit={onSubmit} onClose={() => setShowAuditModal(false)} isSubmitting={submitting} header="Edit Support Contact Information" /> : null}
        </>
    );
}

export const SupportContactScreen = () => {
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const [supportForm, setSupportForm] = React.useState<SupportContactFormModel>();
    const client = useWebPortalApiClient();

    const getData = async () => {
        const countryResult = await client.client.getStudyCountries(undefined, `studyId eq ${study.id}`);

        const support = {
            countries: countryResult.value.map(c => ({
                id: c.id,
                name: c.name,
                tollFreePhone: c.tollFreeNumber,
                localPostalAddress: c.localPostalAddress
            }))
        };


        setSupportForm({ ...support });
    }

    React.useEffect(() => {
        getData();
    }, [client.client]);

    if (!supportForm) return (<LoadingWithMessage message="Loading Support Contact Information..." />);

    return (
        <Authorization currentPermissions={currentUserPermissions} studyId={study.id} permissionId={PermissionEnum.ViewSupportContactInformationAdministration}>
            <DocumentTitle title="Support Contact Information">
                <Form noValidate initialValues={supportForm}>
                    <SupportContactForm form={supportForm} onRefresh={() => getData()} />
                </Form>
            </DocumentTitle>
        </Authorization>
    );
}
